<script setup lang="ts">
import { PartnerCard, type PartnerCardPartner } from '@/features/partners';
import { useContextToasts } from '@glow/shared/composables/useContextToasts';
import { type FragmentType, graphql } from '@/gql';
import { useGetOrCreateUserChat } from '@/features/chat';
import { useRoutes } from '@glow/shared/lib/routes';

const masterFragment = graphql(/* GraphQL */ `
  fragment MasterCard_MasterFragment on Master {
    id
    slug
    fullName
    phone
    email
    about
    avatarUrl
    rating
    isLiked
    reviewCount

    ...SocialFields
  }
`);

const props = defineProps<{
  master: FragmentType<typeof masterFragment>;
  locationIndex?: number;
}>();
const emit = defineEmits<{
  (e: 'update:locationIndex', locationIndex?: number): void;
}>();

const master = useFragmentData(masterFragment, () => props.master);
const { getUrl } = useRoutes();
const partner = computed<PartnerCardPartner>(() => ({
  name: master.value.fullName,
  link: getUrl('partnerDetails', {
    type: 'master',
    partner: master.value.slug,
    city: 'moscow', // FIXME
  }),
  ...master.value,
}));

const { executeMutation: updateLikedState } = useMutation(
  graphql(/** GraphQL */ `
    mutation MasterCard_UpdateMasterLikedState(
      $id: Int!
      $newIsLiked: Boolean!
    ) {
      updateMasterLikedState(id: $id, newIsLiked: $newIsLiked) {
        id
        isLiked
      }
    }
  `)
);
const { showLikedToast } = useContextToasts();

async function onLikedStateUpdate(newIsLiked: boolean) {
  await updateLikedState({ id: master.value.id, newIsLiked });
  showLikedToast(newIsLiked);
}

const locationIndex = ref(props.locationIndex);
watch(
  () => props.locationIndex,
  (id) => (locationIndex.value = id)
);
watch(locationIndex, (index) => emit('update:locationIndex', index));

const { mutate: getOrCreateUserChat, isLoading: isMessageLoading } =
  useGetOrCreateUserChat();
function onMessage() {
  getOrCreateUserChat(
    { userId: master.value.id },
    {
      onSuccess: (response) => {
        navigateTo(getUrl('chat', { id: response.id }));
      },
    }
  );
}
</script>

<template>
  <PartnerCard
    v-model:location-index="locationIndex"
    :partner="partner"
    :is-message-loading="isMessageLoading"
    @message="onMessage"
    @update-liked-state="onLikedStateUpdate"
  />
</template>
